'use client';

import { cn } from '@core/utils';
import type { Ref } from 'react';
import { useState } from 'react';

import {
  RecentSearchChips,
  SearchBar,
  useHideOnScroll,
} from '@/app/_components/SearchBar';

const preventBlur = (e: React.MouseEvent) => e.preventDefault();

interface SearchPageHeaderProps {
  keyword: string;
  filterCount: number;
  recentKeywords: string[];
  onSearch: (keyword: string) => void;
  onFilterClick?: () => void;
  onSelectRecent: (keyword: string) => void;
  onRemoveRecent: (keyword: string) => void;
  onClearRecent: () => void;
  ref?: Ref<HTMLDivElement>;
}

/**
 * 모바일: `position: fixed` 오버레이로 상단 고정 + 스크롤 방향에 따라 translate 숨김.
 * PC: 일반 flow. spacer는 mobile-only.
 * ref는 외부 wrapper에 연결되어 IntersectionObserver의 관찰 대상이 된다
 * (모바일에서는 spacer 높이 = in-flow 기준으로 가시성을 판단).
 */
export const SearchPageHeader = ({
  keyword,
  filterCount,
  recentKeywords,
  onSearch,
  onFilterClick,
  onSelectRecent,
  onRemoveRecent,
  onClearRecent,
  ref,
}: SearchPageHeaderProps) => {
  const [isFocused, setIsFocused] = useState(false);
  const { hidden, atTop } = useHideOnScroll();

  const shouldHide = hidden && !isFocused;
  const shouldHideChipsOnMobile = !isFocused && !atTop;

  return (
    <div ref={ref} className="w-full flex-1">
      <div
        className={cn(
          'flex w-full flex-col items-center gap-12 bg-Gray-white',
          'mobile-tablet:fixed mobile-tablet:inset-x-0 mobile-tablet:top-0 mobile-tablet:z-10 mobile-tablet:gap-0',
          'mobile-tablet:transition-transform mobile-tablet:duration-200',
          shouldHide && 'mobile-tablet:-translate-y-full',
        )}
      >
        <SearchBar
          keyword={keyword}
          filterCount={filterCount}
          sticky={false}
          onSearch={onSearch}
          onFilterClick={onFilterClick}
          onFocusChange={setIsFocused}
        />
        {recentKeywords.length > 0 && (
          <div
            className={cn(
              'w-full overflow-hidden',
              'mobile-tablet:transition-[max-height] mobile-tablet:duration-200 mobile-tablet:ease-out',
              shouldHideChipsOnMobile
                ? 'mobile-tablet:max-h-0'
                : 'mobile-tablet:max-h-48',
            )}
            onMouseDown={preventBlur}
          >
            <RecentSearchChips
              keywords={recentKeywords}
              onSelect={onSelectRecent}
              onRemove={onRemoveRecent}
              onClearAll={onClearRecent}
            />
          </div>
        )}
      </div>
      {/* 모바일: fixed 오버레이 높이만큼 flow에 자리 예약. chips row(최대 48px) 확장 시 96px. */}
      <div
        aria-hidden
        className={cn(
          'hidden mobile-tablet:block mobile-tablet:transition-[height] mobile-tablet:duration-200',
          recentKeywords.length > 0 && !shouldHideChipsOnMobile
            ? 'mobile-tablet:h-88'
            : 'mobile-tablet:h-48',
        )}
      />
    </div>
  );
};
